import {Component, Object3D} from '@wonderlandengine/api';
import {setComponentsActive} from '../utils/activate-children.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {GameManagerInstance, SceneNames} from '../managers/gamemanager.js';
import {ActiveHand} from './active-hand.js';

/**
 * flashlight
 * Start & Select the hand that carries the Flashlight
 */
export class Flashlight extends Component {
    static TypeName = 'flashlight';

    /* Properties that are configurable in the editor */
    @property.object({required: true})
    handR!: Object3D;
    @property.object({required: true})
    handL!: Object3D;
    @property.object({required: true})
    flashlightRight_Loc!: Object3D;
    @property.object({required: true})
    flashlightLeft_Loc!: Object3D;
    @property.object({required: true})
    flashlight!: Object3D;
    @property.object({required: true})
    flashlightNonVR!: Object3D;
    @property.object({required: true})
    activeHandObject!: Object3D;
    @property.object({required: true})
    flashlightLightObject!: Object3D;

    private torchActiveStatus = false;
    private xrActive = false;
    private holdingFlashlightWithRight = true;
    private activeHand?: ActiveHand;
    private isTurnedOn = false;

    start() {
        const tempActiveHand = this.activeHandObject.getComponent(ActiveHand);
        if (!tempActiveHand) {
            throw new Error(
                `flashlight(${this.object.name}):activeHandObject does not have a ActiveHand component`
            );
        }
        this.activeHand = tempActiveHand;

        this.holdingFlashlightWithRight = this.activeHand.activeHand == 'right';

        /* General */
        this.torchActiveStatus = true;
        this.isTurnedOn = false;
        this.updateObjectStates();
    }

    onActivate(): void {
        this.activeHand!.onActiveHandChanged.add(this.onActiveHandChanged);
        this.engine.onXRSessionStart.add(this.onSessionStart);
        GameManagerInstance.onSceneReady.once(this.onSceneReady);
    }

    onDeactivate(): void {
        this.activeHand!.onActiveHandChanged.remove(this.onActiveHandChanged);
        this.engine.onXRSessionStart.remove(this.onSessionStart);
    }

    onActiveHandChanged = (hand: 'left' | 'right') => {
        if (hand === 'left') {
            this.flashlight.parent = this.flashlightLeft_Loc;
            this.holdingFlashlightWithRight = false;
        } else if (hand === 'right') {
            this.holdingFlashlightWithRight = true;
            this.flashlight.parent = this.flashlightRight_Loc;
        }
        this.updateObjectStates();
        this.flashlight.resetPositionRotation();
    }

    onSessionStart = () => {
        this.xrActive = true;
        if (!this.torchActiveStatus) {
            return;
        }
        /* Switch Flashlight to VR */
        this.flashlightNonVR.active = false;

        if (this.flashlight) {
            setComponentsActive(this.flashlight, true);
        } else {
            console.warn('Flashlight: flashlight lost reference');
        }

        this.updateObjectStates();
    }

    onSessionEnd = () => {
        this.xrActive = false;
        if (!this.torchActiveStatus) {
            return;
        }
        /* Switch Flashlight to Non-VR */
        this.flashlightNonVR.active = true;
        this.updateObjectStates();
    }

    onSceneReady = (scene: SceneNames) => {
        if (scene !== SceneNames.Scene1) {
            return;
        }
        if (!this.handL || !this.handR) {
            console.warn('Flashlight: handL or handR lost reference');
            return;
        }
        this.updateObjectStates();
    }

    turnOn() {
        this.isTurnedOn = true;
        this.updateObjectStates();
    }

    turnOff() {
        this.isTurnedOn = false;
        this.updateObjectStates();
    }

    private updateObjectStates() {
        setComponentsActive(this.flashlightLightObject, this.isTurnedOn);
        if (this.xrActive) {
            this.flashlight.active = true;
            this.flashlightNonVR.active = false;
            setComponentsActive(this.flashlight, this.isTurnedOn);
            if (this.isTurnedOn) {
                setComponentsActive(this.handR, !this.holdingFlashlightWithRight);
                setComponentsActive(this.handL, this.holdingFlashlightWithRight);
            } else {
                setComponentsActive(this.handR, true);
                setComponentsActive(this.handL, true);
            }
        } else {
            setComponentsActive(this.flashlight, false);
            this.flashlight.active = false;
            this.flashlightNonVR.active = true;
            setComponentsActive(this.handR, false);
            setComponentsActive(this.handL, false);
        }
    }
}
